/*
 * 工具调用组容器
 *
 * 折叠态:
 *   - 有 running 工具 → 紫色 spinner + 该 running 工具的描述
 *   - 全部 done 但消息仍在 streaming → 紫色 spinner + "思考中…"
 *     (工具刚结束,LLM 还在生成下一段文本/下一轮工具调用的真空期,
 *      避免提前切回静态 ✦ 让用户误以为整轮 React 已完成)
 *   - 全部 done 且消息已结束 → 紫色 ✦ + 最后一个 done 工具的描述
 *   思考态(无 tool-call、纯流式)由 ChatPanel 的 ThinkingPlaceholder 单独渲染.
 *
 * 默认始终折叠,用户点击头部才展开 timeline. 流式中也不自动展开 —— 折叠态的
 * spinner + 工具名已经能体现当前进度,timeline 是给关心细节的人主动看的.
 * 例外: 有长时工具带 done/total 进度时 (大批量填表/填图), 把进度 (阶段名 + N/M + %)
 * + 一条细进度条提到折叠头上, 不用展开就能看见实时进度 (原来埋在展开 body 里, 用户反馈看不到).
 */

import React, { useEffect, useRef, useState } from 'react'
import { Progress } from 'antd'
import type { MessagePart } from '../../types'
import { ChevronDownIcon, LoaderIcon, XIcon } from './icons'
import ToolCallInline from './ToolCallInline'
import './ToolCallGroup.scss'

type ToolCallPart = Extract<MessagePart, { type: 'tool-call' }>

interface Props {
  calls: ToolCallPart[]
}

function isRunning(part: ToolCallPart): boolean {
  return part.state === 'input-streaming' || part.state === 'input-available'
}

// ⚡ ToolCallGroup 用 React.memo + **自定义 fingerprint 比较**.
//
// 为什么不能用默认浅比较: 父组件 (ChatPanel.MessageBubble) 内
// `const blocks = groupParts(message.parts)` 每次跑都 new 一个数组,
// `prev.calls === next.calls` 永远 false → 默认浅比较等于没 memo.
//
// 但我们想跳过的真实场景是: streaming 中那条 message, parts 加了一个新 text
// chunk 但**已有的工具组里所有 tool 引用都没变** (没新 tool / 没状态变化) →
// 整个 ToolCallGroup 子树不该 reconcile.
//
// 解法: 自定义比较函数 → 逐项比 calls[i] 引用. 已完成 tool 引用稳定 → 跳过.
// 任何一个 tool 的 part 引用变 (state / args / output / errorText 更新) →
// 整个 group re-render, 内部 ToolCallInline 通过自己的 memo 进一步只让"真正变了
// 的那个" inline 重 render body.
//
// 这是"双层 memo" — 父层用 fingerprint 拦住 99% 不必要重渲, 子层用浅比较
// 在 1% 必须 re-render 时也只精确 update 那一项.
//
// iOS 类比: 类似 NSDiffableDataSourceSnapshot 的 itemIdentifiers + reloadItems —
// 只 reload 真正 identifier 变化的 cell, 而不是 reloadData 全部.
const ToolCallGroupImpl: React.FC<Props> = ({ calls }) => {
  const [open, setOpen] = useState(false)

  if (calls.length === 0) return null

  // 组里只要有任一工具在跑就算"运行中"(spinner + shimmer);
  // 整轮真空期的"正在处理"由 ChatPanel 末尾的全局指示器承载, 工具组不掺和.
  const pivotRunning = calls.some(isRunning)
  const showSpinner = pivotRunning
  // 任何工具 output-error / incomplete → ✗(红);否则 → ✓(绿)
  const anyError = calls.some(
    (c) => c.state === 'output-error' || c.state === 'incomplete'
  )
  // 有 running 工具带 done/total 进度 → 把进度提到折叠头 (不用展开就看见实时 N/M·%).
  // 填表/填图工具串行执行, 实际同时最多一个有进度, 取第一个即可.
  const running = calls.find(
    (c) => isRunning(c) && c.progress && c.progress.total > 0
  )
  const activeProgress = running?.progress
  const activePct = activeProgress
    ? Math.min(
        100,
        Math.round((activeProgress.done / activeProgress.total) * 100)
      )
    : 0
  // 折叠摘要: 有活跃进度时直接显示进度 (label 是后端给的阶段名, 如"下载图片"/"嵌入表格");
  // 否则走通用"处理过程 · N 个工具"(避免"工具调用"这种 jargon, 普通用户更易懂).
  const summary = activeProgress
    ? `${activeProgress.label || '处理中'} · ${activeProgress.done}/${
        activeProgress.total
      } · ${activePct}%`
    : `处理过程 · ${calls.length} 个工具`

  return (
    <div className="aiClerk-tool-group">
      <button
        type="button"
        className="aiClerk-tool-group__header"
        onClick={() => setOpen((v) => !v)}
      >
        {/* 完成态不再显示 ✓(过程注脚, 不喧宾夺主); 仅运行中 spinner / 出错 ✗ */}
        {showSpinner ? (
          <LoaderIcon className="aiClerk-tool-group__spark is-spinning" />
        ) : anyError ? (
          <XIcon className="aiClerk-tool-group__spark is-error" />
        ) : null}
        <span
          className={
            'aiClerk-tool-group__summary' +
            // running 用紫色 shimmer 强调"正在动"
            (pivotRunning ? ' aiClerk-tool-group__summary--running' : '')
          }
        >
          {summary}
        </span>
        <ChevronDownIcon
          className={'aiClerk-tool-group__chev' + (open ? ' is-open' : '')}
        />
      </button>
      {/* 活跃进度条提到折叠头下方 (常驻可见, 不在折叠 body 里) —— 大批量填表/填图实时进度.
          放 button 外 (antd Progress 含交互内容, 不能嵌在 button 里). */}
      {activeProgress ? (
        <div
          className="aiClerk-tool-group__header-progress"
          style={{ padding: '0 12px 8px 34px' }}
        >
          <Progress
            percent={activePct}
            status="active"
            strokeWidth={5}
            showInfo={false}
          />
        </div>
      ) : null}
      <div className={'aiClerk-tool-group__body' + (open ? ' is-open' : '')}>
        {/* body-inner:grid 折叠动画的裁剪层;timeline:真正可滚动的列表 */}
        <div className="aiClerk-tool-group__body-inner">
          <div className="aiClerk-tool-group__timeline">
            {calls.map((c, i) => (
              <ToolCallInline
                key={c.toolCallId}
                part={c}
                isLast={i === calls.length - 1}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * 自定义比较: 逐项比 calls[i] 引用相等. 长度变 / 任何一个 tool 引用变都 re-render.
 * 浅比较成本 O(n), n 通常 < 10, 远小于跑一遍 body + reconcile timeline.
 */
function areToolCallGroupPropsEqual(prev: Props, next: Props): boolean {
  if (prev.calls === next.calls) return true
  if (prev.calls.length !== next.calls.length) return false
  for (let i = 0; i < prev.calls.length; i++) {
    if (prev.calls[i] !== next.calls[i]) return false
  }
  return true
}

export const ToolCallGroup = React.memo(
  ToolCallGroupImpl,
  areToolCallGroupPropsEqual
)
ToolCallGroup.displayName = 'ToolCallGroup'

/**
 * 思考态占位 —— LLM 流式中、但当前没有 running 工具 / 没有正在 stream 的文本时,
 * 用工具组同款样式显示「spinner + 思考中…」, 不可展开.
 *
 * latestThought (可选): 最新一段 reasoning 全文 (调用方已剥下划线 + 截断),
 * 多行展示在 spinner 下面,有 max-height + 自动滚到底.
 *
 * 由 ChatPanel 在合适的时机渲染, ToolCallGroup 本身仍然 calls.length === 0 → null.
 */
export const ThinkingPlaceholder: React.FC<{
  latestThought?: string
  /** 文案, 默认"思考中…"; 消息流末尾的全局进度指示器传"正在处理" */
  label?: string
}> = ({ latestThought, label = '思考中…' }) => (
  <div className="aiClerk-tool-group aiClerk-tool-group--thinking-placeholder">
    <div className="aiClerk-tool-group__header" aria-disabled>
      <LoaderIcon className="aiClerk-tool-group__spark is-spinning" />
      <span className="aiClerk-tool-group__summary aiClerk-tool-group__summary--muted">
        {label}
      </span>
    </div>
    {latestThought && <ThoughtTail text={latestThought} />}
  </div>
)

/** 思考摘要副容器: 多行 + max-height 滚动 + 自动跟随到底.
 *  text 每次变化时 scrollTop = scrollHeight, 让用户看到最新流入的 reasoning.
 *  用户主动向上滚后, 流式更新仍会强制回到底 (简化版, 不做"用户滚走则停 follow"). */
const ThoughtTail: React.FC<{ text: string }> = ({ text }) => {
  const ref = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const el = ref.current
    if (el) el.scrollTop = el.scrollHeight
  }, [text])
  return (
    <div ref={ref} className="aiClerk-tool-group__thought" title={text}>
      {text}
    </div>
  )
}

export default ToolCallGroup
